/**
 * The column targets an import can write, and the template built from them.
 *
 * MOVED HERE FROM `import-run.tsx` so two screens can share one list. The
 * mapping picker offers these -- plus, for a `spec:`-prefixed column, a target of
 * its own -- and the dashboard's template download is generated from the same
 * array, so a writable field can never appear in one and not the other.
 * A hand-kept second copy is exactly how the JSON-LD extractor came to be
 * unreachable through the only route that reaches it.
 *
 * `import-contract.test.ts` in the backend reads this file and asserts the list
 * equals `IMPORT_FIELD_KINDS` on the server. That is what makes the generated
 * template correct rather than merely plausible.
 */
export const FIELD_TARGETS = [
	{ value: "", label: "— ignore —" },
	{ value: "brandSku", label: "Brand SKU (the brand's own code)" },
	{ value: "brandSkuKind", label: "Kind of code" },
	{ value: "name", label: "Name" },
	{ value: "description", label: "Description" },
	{ value: "categorySlug", label: "Category slug" },
	{ value: "sourceCategory", label: "Category, in the source's own words" },
	{ value: "uom", label: "Unit" },
	{ value: "tier", label: "Tier" },
	{ value: "hsnCode", label: "HSN code" },
	{ value: "price", label: "Price" },
	/*
	 * Restored by plan 013 U2, which built the variant resolution the commit
	 * was missing. They were removed when the commit wrote none of them; the
	 * backend contract test is what keeps this list and the server's honest.
	 */
	{ value: "imageUrls", label: "Image URLs (separated by |)" },
	{ value: "variantSku", label: "Variant code (the brand's own)" },
	{ value: "variantAxis1Name", label: "Variant axis 1 name (e.g. Finish)" },
	{ value: "variantAxis1Value", label: "Variant axis 1 value (e.g. Chrome)" },
	{ value: "variantAxis2Name", label: "Variant axis 2 name" },
	{ value: "variantAxis2Value", label: "Variant axis 2 value" },
	{ value: "packQty", label: "Pack quantity" },
];

/**
 * Specification columns, which are a convention rather than a fixed list.
 *
 * Any column prefixed `spec:` lands in the product's specifications, so these
 * three are illustrative and an operator is expected to change them to whatever
 * their category's template actually defines. They are in the template because a
 * convention nobody demonstrates is a convention nobody uses.
 *
 * These three ARE the `tiles` template's three required attributes, which is why
 * they are the ones shown.
 */
const SPEC_EXAMPLE_COLUMNS = ["spec:Material", "spec:Size", "spec:Finish"];

/**
 * Example rows, on a category that exists.
 *
 * `tiles` is a real leaf and is deliberately not invented: an earlier draft of
 * the written template used `ceramic-tile`, which reads exactly like a real slug
 * and is a FIXTURE from a taxonomy test. Every row of an import filled from that
 * template would have failed on an unknown category.
 *
 * The third row leaves the price empty on purpose. Published lists print "price
 * available on request" over whole sections, and a row with no price is a
 * legitimate product record rather than a mistake to be filled with a zero.
 *
 * SPEC VALUES ARE MATCHED CASE-SENSITIVELY against the category template's
 * `enumValues` -- `validateSpecifications` does an exact `includes`. The first
 * draft of these rows wrote `Vitrified`, `Glossy`, `Matt`, `Glass mosaic` and
 * `Textured`, and every one of them would have been refused: the `tiles`
 * template declares `vitrified | ceramic | porcelain | natural_stone` and
 * `matte | glossy | anti_skid | satin`. A template whose own example rows fail
 * validation teaches the operator the wrong thing on their first import.
 */
const EXAMPLE_ROWS = [
	[
		"KAJ-FLOOR-600",
		"article_no",
		"Vitrified floor tile 600x600 ivory",
		"Double-charge vitrified tile for interior floors",
		"tiles",
		"Floor Tiles",
		"sq_ft",
		"standard",
		"6907",
		"1,250.00",
		"https://brand.example/floor-600-ivory.jpg|https://brand.example/floor-600-ivory-room.jpg",
		"KAJ-FLOOR-600-IVORY",
		"Finish",
		"glossy",
		"",
		"",
		"4",
		"vitrified",
		"600x600 mm",
		"glossy",
	],
	[
		"KAJ-WALL-300",
		"article_no",
		"Ceramic wall tile 300x600 white",
		"Glazed ceramic wall tile",
		"tiles",
		"Wall Tiles",
		"sq_ft",
		"standard",
		"6907",
		"845",
		"https://brand.example/wall-300-white.jpg",
		"KAJ-WALL-300-WHITE",
		"Finish",
		"matte",
		"",
		"",
		"6",
		"ceramic",
		"300x600 mm",
		"matte",
	],
	[
		"KAJ-ASK-001",
		"design_code",
		"Designer mosaic panel",
		"Price shown on request in the source list",
		"tiles",
		"Mosaic Panels",
		"sq_ft",
		"premium",
		"6907",
		"",
		"",
		"KAJ-ASK-001-GOLD",
		"Finish",
		"satin",
		"",
		"",
		"1",
		"ceramic",
		"300x300 mm",
		"anti_skid",
	],
];

/** The header the template carries: every writable field, then the spec examples. */
export function templateColumns(): string[] {
	return [
		...FIELD_TARGETS.map((target) => target.value).filter(Boolean),
		...SPEC_EXAMPLE_COLUMNS,
	];
}

/** RFC 4180: quote when the value contains a comma, quote or newline. */
function csvCell(value: string): string {
	if (!/[",\n\r]/.test(value)) return value;
	return `"${value.replace(/"/g, '""')}"`;
}

/**
 * The template file's contents.
 *
 * CRLF line endings, because the most likely thing to open this is Excel on
 * Windows, and the reader on the other side accepts either.
 */
export function buildImportTemplateCsv(): string {
	const rows = [templateColumns(), ...EXAMPLE_ROWS];
	return `${rows.map((row) => row.map(csvCell).join(",")).join("\r\n")}\r\n`;
}
